import React, { useCallback, useEffect, useRef, useState } from "react";

// CSS
import "./../../../assets/css/purchase/datatable.css";

// Utils
import moment from "moment";
import { exportHtmlTableToExcel } from "../../../utils/ExportUtils";
import { formatCurrency, getAlert, parseCurrency } from "../../../utils/SamuiUtils";
import { formatDate } from "../../../utils/DateUtils";
import Pagination from "../../components/Footer/Pagination";

const REPORT_META = {
  "/sales-report": {
    title: "รายงานยอดซื้อ-ขายประจำวัน",
    filePrefix: "sales-report",
  },
  "/inventory-movement-report": {
    title: "รายงานสินค้าเคลื่อนไหว",
    filePrefix: "inventory-movement-report",
  },
  "/stock-balance-report": {
    title: "รายงานสินค้าคงเหลือประจำวัน",
    filePrefix: "stock-balance-report",
  },
  "/loan-crate-report": {
    title: "รายงานยืมลังประจำวัน",
    filePrefix: "loan-crate-report",
  },
  "/deposit-report": {
    title: "รายงานมัดจำประจำวัน",
    filePrefix: "deposit-report",
  },
  "/overdue-loan-crate-report": {
    title: "รายงานลูกค้ายืมลังแล้วไม่สั่ง",
    filePrefix: "overdue-loan-crate-report",
  },
  "/receivables-report": {
    title: "รายงานลูกหนี้รายวัน",
    filePrefix: "receivables-report",
  },
  "/payables-report": {
    title: "รายงานเจ้าหนี้รายวัน",
    filePrefix: "payables-report",
  },
  "/vansale-report": {
    title: "รายงานนำส่งรายได้",
    filePrefix: "vansale-report",
  },
  "/internal-material-requisition-report": {
    title: "รายงานเบิกวัสดุภายใน",
    filePrefix: "internal-material-requisition-report",
  },
  default: {
    title: "รายงาน",
    filePrefix: "report",
  },
};

const ReportsTable = ({
  currentItems: paginatedItems,
  currentPage,
  handlePageChange,
  dataMasterList,
  itemsPerPage,
  fieldMappings,
  setCurrentPage,
  setItemsPerPage,
  onExportReady,
  showExportButton = true,
}) => {
  const tableRef = useRef(null);
  // State สำหรับเก็บ ID แถวที่เลือก
  const [selectedRow, setSelectedRow] = useState(null);
  const [itemsPerPageDropdown, setItemsPerPageDropdown] =
    useState(itemsPerPage);
  const [isExporting, setIsExporting] = useState(false);
  const [exportAllMode, setExportAllMode] = useState(false);

  const isIce = typeof window !== "undefined" && localStorage.getItem("company") === '2';

  // คำนวณการแสดงผลข้อมูล
  const startIndex = (currentPage - 1) * itemsPerPageDropdown + 1;
  const endIndex = Math.min(
    currentPage * itemsPerPageDropdown,
    dataMasterList.length
  );

  // ปรับให้ตัวแปร `currentItems` ชี้ไปที่ข้อมูลที่จะแสดง (หน้าปัจจุบัน หรือ ทั้งหมดเมื่อส่งออก)
  const currentItems = exportAllMode ? dataMasterList : paginatedItems;

  // ฟังก์ชันสำหรับการเปลี่ยนจำนวนแถวต่อหน้า
  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseCurrency(event.target.value);
    setItemsPerPageDropdown(newItemsPerPage);
    setItemsPerPage(newItemsPerPage);
    handlePageChange(1);
  };

  const getReportMeta = useCallback(() => {
    if (typeof window === "undefined") {
      return REPORT_META.default;
    }
    return REPORT_META[window.location.pathname] || REPORT_META.default;
  }, []);

  const handleExport = useCallback(async () => {
    if (!dataMasterList || dataMasterList.length === 0) {
      getAlert("FAILED", "ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    if (!tableRef.current) {
      getAlert("FAILED", "ไม่พบตารางข้อมูลสำหรับ Export");
      return;
    }

    const { title, filePrefix } = getReportMeta();
    const timeStamp = moment().format("YYYYMMDD_HHmmss");
    const fileName = `${filePrefix}_${timeStamp}.xlsx`;

    try {
      setIsExporting(true);
      // Render full dataset into the table temporarily so exportHtmlTableToExcel
      // captures all rows (not just current page)
      setExportAllMode(true);
      // wait for DOM to update with full rows
      await new Promise((resolve) => setTimeout(resolve, 50));
      await exportHtmlTableToExcel(tableRef.current, {
        sheetName: title,
        fileName,
        headerLines: [title],
      });
      // restore pagination rendering
      setExportAllMode(false);
    } catch (error) {
      console.error("Export report table error:", error);
      getAlert("FAILED", "ไม่สามารถ Export ข้อมูลได้");
    } finally {
      setIsExporting(false);
    }
  }, [dataMasterList, getReportMeta]);

  const hasData = Array.isArray(dataMasterList) && dataMasterList.length > 0;

  useEffect(() => {
    if (typeof onExportReady === "function") {
      onExportReady({
        exportToExcel: handleExport,
        isExporting,
        hasData,
      });
    }
  }, [onExportReady, handleExport, isExporting, hasData]);

  return (
    <div className="col-md-12 mt-4">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4>ผลการค้นหา</h4>
          {showExportButton && (
            <button
              type="button"
              className="btn btn-sm text-white"
              style={{ backgroundColor: "#2E7D32" }}
              onClick={handleExport}
              disabled={
                isExporting || !dataMasterList || dataMasterList.length === 0
              }
            >
              <i className="fas fa-file-excel" />{" "}
              {isExporting ? "กำลังส่งออก..." : "Export"}
            </button>
          )}
        </div>
        <div className="card-body">
          {/* DataTable */}
          <div
            className="table-responsive"
            style={{ maxHeight: "550px", overflowY: "auto" }}
          >
            <table
              id="basic-datatables"
              className="table table-striped table-hover"
              ref={tableRef}
            >
              <thead className="thead-dark">
                {/* 1. รายงานยอดซื้อ / ขายประจำวัน */}
                {window.location.pathname === "/sales-report" && (
                  <tr>
                    <th className="text-center">เลขที่เอกสาร</th>
                    <th className="text-center">ประเภท</th>
                    <th className="text-center">สถานะจ่าย</th>
                    <th className="text-center">ประเภทการจ่าย</th>
                    <th className="text-center">รหัสลูกค้า</th>
                    <th className="text-center">ชื่อลูกค้า</th>
                    <th className="text-center">สายส่ง</th>
                    <th className="text-center">ยอดขาย</th>
                    <th className="text-center">VAT</th>
                    <th className="text-center">รวมทั้งหมด</th>
                    <th className="text-center">ยอดชำระ</th>
                  </tr>
                )}

                {/* 2.1 รายงาน inventory movement (แยกตามแผนก/คลัง)  */}
                {window.location.pathname === "/inventory-movement-report" && (
                  <tr>
                    <th className="text-center">วันที่สต๊อก</th>
                    <th className="text-center">รหัสสินค้า</th>
                    <th className="text-center">ชื่อสินค้า</th>
                    <th className="text-center">ประเภท</th>
                    <th className="text-center">จำนวน</th>
                    <th className="text-center">เลขเอกสาร</th>
                    <th className="text-center">เอกสารอ้างอิง</th>
                    <th className="text-center">หมายเหตุ</th>
                  </tr>
                )}

                {/* 2.2 รายงานสินค้าคงเหลือประจำวัน แยกตามคลัง / แยกตามสินค้า  */}
                {window.location.pathname === "/stock-balance-report" && (
                  <tr>
                    <th className="text-center">รหัสสินค้า</th>
                    <th className="text-center">ชื่อสินค้า</th>
                    <th className="text-center">จำนวน</th>
                    <th className="text-center">หน่วย</th>
                  </tr>
                )}

                {/* 3. รายงานยืมลังประจำวัน */}
                {window.location.pathname === "/loan-crate-report" && (
                  <tr>
                    <th className="text-center">วันที่</th>
                    <th className="text-center">เลขที่ใบยืมสินค้า</th>
                    <th className="text-center">สถานะ</th>
                    <th className="text-center">รหัสลูกค้า</th>
                    <th className="text-center">ชื่อลูกค้า</th>
                    <th className="text-center">รหัสสินค้า</th>
                    <th className="text-center">ชื่อสินค้า</th>
                    <th className="text-center">จำนวน</th>
                    <th className="text-center">หน่วย</th>
                  </tr>
                )}

                {/* 4. รายงานมัดจำประจำวัน */}
                {window.location.pathname === "/deposit-report" && (
                  <tr>
                    <th className="text-center">วันที่เอกสาร</th>
                    <th className="text-center">เลขที่เอกสาร</th>
                    <th className="text-center">สถานะ</th>
                    <th className="text-center">รหัสลูกค้า</th>
                    <th className="text-center">ชื่อลูกค้า</th>
                    <th className="text-center">จำนวนเงิน</th>
                  </tr>
                )}

                {/* 5. รายงานลูกค้ายืมลัง แล้วไม่สั่งเกินสัปดาห์ */}
                {window.location.pathname === "/overdue-loan-crate-report" && (
                  <tr>
                    <th className="text-center">รหัสลูกค้า</th>
                    <th className="text-center">ชื่อลูกค้า</th>
                    <th className="text-center">วันที่สั่งล่าสุด</th>
                    <th className="text-center">จำนวนลังที่ยืม</th>
                    <th className="text-center">รหัสลังที่ยืม</th>
                  </tr>
                )}

                {/* 6.1 รายงานลูกหนี้ รายวัน */}
                {window.location.pathname === "/receivables-report" && (
                  <tr>
                    <th className="text-center">วันที่เอกสาร</th>
                    <th className="text-center">ประเภท</th>
                    <th className="text-center">สายรถ</th>
                    <th className="text-center">เลขที่เอกสาร</th>
                    <th className="text-center">รหัสลูกค้า</th>
                    <th className="text-center">ชื่อลูกค้า</th>
                    <th className="text-center">จำนวนเงิน</th>
                  </tr>
                )}

                {/* 6.2 รายงานเจ้าหนี้ รายวัน */}
                {window.location.pathname === "/payables-report" && (
                  <tr>
                    <th className="text-center">วันที่เอกสาร</th>
                    <th className="text-center">เลขที่เอกสาร</th>
                    <th className="text-center">ประเภท</th>
                    <th className="text-center">ประเภทการจ่าย</th>
                    <th className="text-center">รหัสเจ้าหนี้</th>
                    <th className="text-center">ชื่อเจ้าหนี้</th>
                    <th className="text-center">รายจ่ายสุทธิ</th>
                  </tr>
                )}

                {/* 6.3 รายงานนำส่งรายได้ */}
                {window.location.pathname === "/vansale-report" && (
                  <>
                    <tr>
                      <th rowSpan={2} className="text-center">
                        เลขที่ใบขาย
                      </th>
                      <th rowSpan={2} className="text-center">
                        รายชื่อลูกค้า
                      </th>
                      <th colSpan={5} className="text-center">
                        {isIce ? "รายการสินค้าโรงน้ำแข็ง" : "รายการสินค้า IDRINK"}
                      </th>
                      <th colSpan={3} className="text-center">
                        ยอดขาย
                      </th>
                      <th colSpan={5} className="text-center">
                        การชำระ
                      </th>
                      <th rowSpan={2} className="text-center">
                        ผู้จัดส่ง
                      </th>
                      <th rowSpan={2} className="text-center">
                        ช่วงส่ง
                      </th>
                    </tr>
                    <tr>
                      <th className="text-center">{isIce ? "หลอดใหญ่" : "OEM"}</th>
                      <th className="text-center">{isIce ? "หลอดเล็ก" : "350ml"}</th>
                      <th className="text-center">{isIce ? "บดใหญ่" : "600ml"}</th>
                      <th className="text-center">{isIce ? "บดเล็ก" : "1500ml"}</th>
                      <th className="text-center">{isIce ? "แพ็คถุง" : "น้ำถัง"}</th>
                      <th className="text-center">ยอดขาย</th>
                      <th className="text-center">Vat</th>
                      <th className="text-center">รวมยอดทั้งหมด</th>
                      <th className="text-center">เงินสด</th>
                      <th className="text-center">โอน</th>
                      <th className="text-center">เครดิต</th>
                      <th className="text-center">เช็ค</th>
                      <th className="text-center">ค้างจ่าย</th>
                    </tr>
                  </>
                )}

                {/* 6.5 รายงานเบิกวัสดุภายใน */}
                {window.location.pathname ===
                  "/internal-material-requisition-report" && (
                    <tr>
                      <th className="text-center">ลำดับที่</th>
                      <th className="text-center">เลขที่ใบเบิกสินค้า</th>
                      <th className="text-center">รหัสวัสดุ</th>
                      <th className="text-center">รายการวัสดุ</th>
                      <th className="text-center">รายละเอียดสินค้า</th>
                      <th className="text-center">คลังที่</th>
                      <th className="text-center">กลุ่มวัสดุ</th>
                      <th className="text-center">จำนวน</th>
                      <th className="text-center">ราคาหน่วย</th>
                      <th className="text-center">หน่วย</th>
                      <th className="text-center">จำนวนเงิน</th>
                      <th className="text-center">ผู้เบิก</th>
                      <th className="text-center">แผนก</th>
                      <th className="text-center">หมายเหตุ</th>
                    </tr>
                  )}
              </thead>
              <tbody>
                {/* 1. รายงานยอดซื้อ / ขายประจำวัน */}
                {window.location.pathname === "/sales-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.Doc_No] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* เลขที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.Doc_No] || "-"}
                          </td>

                          {/* ประเภท */}
                          <td className="text-center">
                            {data[fieldMappings.SO_Type] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[fieldMappings.SO_Type] === "Admin"
                                      ? "#ffc107"
                                      : "#13A10E",
                                  color: "white",
                                  width: "100%",
                                  fontSize: "12px",
                                  border: "none",
                                }}
                              >
                                {data[fieldMappings.SO_Type]}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* สถานะจ่าย */}
                          <td className="text-center">
                            {data[fieldMappings.DocStatusPaid_Name] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[fieldMappings.DocStatusPaid_Colour] ||
                                    "#6c757d",
                                  color: "white",
                                  width: "100%",
                                  fontSize: "12px",
                                  border: "none",
                                }}
                              >
                                {data[fieldMappings.DocStatusPaid_Name]}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* ประเภทการจ่าย */}
                          <td className="text-center">
                            {data[fieldMappings.DocStatusPaid_Type_Name] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[
                                    fieldMappings.DocStatusPaidType_Colour
                                    ] || "#6c757d",
                                  color: "white",
                                  width: "100%",
                                  fontSize: "12px",
                                  border: "none",
                                }}
                              >
                                {data[fieldMappings.DocStatusPaid_Type_Name]}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* รหัสลูกค้า */}
                          <td className="text-center">
                            {data[fieldMappings.AR_ID] || "-"}
                          </td>

                          {/* ชื่อลูกค้า */}
                          <td className="text-left">
                            {data[fieldMappings.AR_Name] || "-"}
                          </td>

                          {/* สายส่ง */}
                          <td className="text-center">
                            {data[fieldMappings.DL_LineName] || "-"}
                          </td>

                          {/* ยอดขาย */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.Total_Before_Vat] || 0.0
                            )}
                          </td>

                          {/* VAT */}
                          <td className="text-end">
                            {formatCurrency(data[fieldMappings.VAT] || 0.0)}
                          </td>

                          {/* NetTotal */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.NetTotal] || 0.0
                            )}
                          </td>

                          {/* PayTotal */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.Pay_Total] || 0.0
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={11}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {/* 2.1 รายงาน inventory movement (แยกตามแผนก/คลัง)  */}
                {window.location.pathname === "/inventory-movement-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.docNo] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* วันที่สต๊อก */}
                          <td className="text-center">
                            {data[fieldMappings.stcDate]
                              ? formatDate(data[fieldMappings.stcDate])
                              : "-"}
                          </td>

                          {/* รหัสสินค้า */}
                          <td className="text-center">
                            {data[fieldMappings.itemCode] || "-"}
                          </td>

                          {/* รหัสสินค้า */}
                          <td className="text-left">
                            {data[fieldMappings.itemName] || "-"}
                          </td>

                          {/* ประเภท */}
                          <td className="text-center">
                            {data[fieldMappings.docType] || "-"}
                          </td>

                          {/* จำนวน */}
                          <td className="text-center">
                            {data[fieldMappings.stcQty] || "-"}
                          </td>

                          {/* เลขเอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.docNo] || "-"}
                          </td>

                          {/* เอกสารอ้างอิง */}
                          <td className="text-center">
                            {data[fieldMappings.docNoRef] || "-"}
                          </td>

                          {/* หมายเหตุ */}
                          <td className="text-left">
                            {data[fieldMappings.stcRemark] || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {/* 2.1 รายงาน inventory movement (แยกตามแผนก/คลัง)  */}
                {window.location.pathname === "/stock-balance-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* รหัสสินค้า */}
                          <td className="text-center">
                            {data[fieldMappings.itemCode] || "-"}
                          </td>

                          {/* รหัสสินค้า */}
                          <td className="text-left">
                            {data[fieldMappings.itemName] || "-"}
                          </td>

                          {/* จำนวน */}
                          <td className="text-center">
                            {data[fieldMappings.itemOnHand] || "-"}
                          </td>

                          {/* หน่วย */}
                          <td className="text-center">
                            {data[fieldMappings.itemUnit] || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}

                {/* 3. รายงานยืมลังประจำวัน */}
                {window.location.pathname === "/loan-crate-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* วันที่ */}
                          <td className="text-center">
                            {data[fieldMappings.whDocDate] || "-"}
                          </td>

                          {/* เลขที่ใบยืมสินค้า */}
                          <td className="text-center">
                            {data[fieldMappings.whDocNo] || "-"}
                          </td>

                          {/* สถานะ */}
                          <td className="text-center">
                            {data[fieldMappings.returnStatus] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[fieldMappings.returnStatus] ===
                                      "คืนแล้ว"
                                      ? "#28a745"
                                      : "#dc3545",
                                  color: "white",
                                  width: "100%",
                                  fontSize: "12px",
                                  border: "none",
                                }}
                              >
                                {data[fieldMappings.returnStatus]}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* รหัสลูกค้า */}
                          <td className="text-center">
                            {data[fieldMappings.tWhCode] || "-"}
                          </td>

                          {/* ชื่อลูกค้า */}
                          <td className="text-left">
                            {data[fieldMappings.tWhName] || "-"}
                          </td>

                          {/* รหัสสินค้า */}
                          <td className="text-center">
                            {data[fieldMappings.itemCode] || "-"}
                          </td>

                          {/* ชื่อสินค้า */}
                          <td className="text-left">
                            {data[fieldMappings.itemName] || "-"}
                          </td>

                          {/* จำนวน */}
                          <td className="text-center">
                            {data[fieldMappings.itemQty] || "-"}
                          </td>

                          {/* หน่วย */}
                          <td className="text-center">
                            {data[fieldMappings.itemUnit] || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}

                {window.location.pathname === "/loan-crate-report" &&
                  currentItems.length > 0 && (
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        fontWeight: "bold",
                        borderTop: "2px solid #dee2e6",
                      }}
                    >
                      <td
                        className="text-end"
                        colSpan={7}
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        ยอดรวม
                      </td>
                      {/* จำนวนรวม */}
                      <td
                        className="text-center"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {currentItems
                          .reduce(
                            (sum, item) =>
                              sum +
                              (parseFloat(item[fieldMappings.itemQty]) || 0),
                            0
                          )
                          .toLocaleString()}
                      </td>
                      {/* คอลัมน์หน่วยว่าง */}
                      <td className="text-center">-</td>
                    </tr>
                  )}
                {/* 4. รายงานมัดจำประจำวัน */}
                {window.location.pathname === "/deposit-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.docNo] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* วันที่่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.docDate]
                              ? formatDate(data[fieldMappings.docDate])
                              : "-"}
                          </td>

                          {/* เลขที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.docNo] || "-"}
                          </td>

                          {/* สถานะ */}
                          <td className="text-center">
                            {data[fieldMappings.docStatusName] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[fieldMappings.docStatusColor],
                                  color: "white",
                                  width: "100%",
                                }}
                              >
                                {data[fieldMappings.docStatusName]}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* รหัสลูกค้า */}
                          <td className="text-center">
                            {data[fieldMappings.arCode] || "-"}
                          </td>

                          {/* ชื่อลูกค้า */}
                          <td className="text-left">
                            {data[fieldMappings.arName] || "-"}
                          </td>

                          {/* ยอดขาย */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.netTotal] || 0.0
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {/* เพิ่มแถวสรุปยอดรวมสำหรับ deposit-report */}
                {window.location.pathname === "/deposit-report" &&
                  currentItems.length > 0 && (
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        fontWeight: "bold",
                        borderTop: "2px solid #dee2e6",
                      }}
                    >
                      <td
                        className="text-end"
                        colSpan={5}
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        ยอดรวม
                      </td>
                      {/* จำนวนเงินรวม */}
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) =>
                              sum + (item[fieldMappings.netTotal] || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  )}

                {/* 5. รายงานลูกค้ายืมลัง แล้วไม่สั่งเกินสัปดาห์ */}
                {window.location.pathname === "/overdue-loan-crate-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.arCode] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* รหัสลูกค้า */}
                          <td className="text-center">
                            {data[fieldMappings.arCode] || "-"}
                          </td>

                          {/* ชื่อลูกค้า */}
                          <td className="text-left">
                            {data[fieldMappings.arName] || "-"}
                          </td>

                          {/* วันที่ทำรายการล่าสุด */}
                          <td className="text-center">
                            {data[fieldMappings.lastDate] || "-"}
                          </td>

                          {/* จำนวน */}
                          <td className="text-end">
                            {formatCurrency(data[fieldMappings.amount]) || "-"}
                          </td>

                          {/* วันที่ทำรายการล่าสุด */}
                          <td className="text-left">
                            {data[fieldMappings.ibNo] || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={11}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}

                {/* 6.1 รายงานลูกหนี้ รายวัน */}
                {window.location.pathname === "/receivables-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.docNo] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* วันที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.docDate]
                              ? data[fieldMappings.docDate]
                              : "-"}
                          </td>

                          {/* ประเภท */}
                          <td className="text-center">
                            {data[fieldMappings.soType] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[fieldMappings.soType] === "S"
                                      ? "#ffc107" // Admin
                                      : "#13A10E", // Vansale
                                  color: "white",
                                  width: "100%",
                                  fontSize: "12px",
                                  border: "none",
                                }}
                              >
                                {data[fieldMappings.soType] === "S"
                                  ? "Admin"
                                  : "Vansale"}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* เลขที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.dlLineName] || "-"}
                          </td>

                          {/* เลขที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.docNo] || "-"}
                          </td>

                          {/* รหัสลูกค้า */}
                          <td className="text-center">
                            {data[fieldMappings.arCode] || "-"}
                          </td>

                          {/* ชื่อลูกค้า */}
                          <td className="text-left">
                            {data[fieldMappings.arName] || "-"}
                          </td>

                          {/* จำนวนเงิน */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.netTotal] || 0.0
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {window.location.pathname === "/receivables-report" &&
                  currentItems.length > 0 && (
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        fontWeight: "bold",
                        borderTop: "2px solid #dee2e6",
                      }}
                    >
                      <td
                        className="text-end"
                        colSpan={6}
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        ยอดรวม
                      </td>
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) =>
                              sum +
                              (parseFloat(item[fieldMappings.netTotal]) || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  )}
                {/* 6.2 รายงานเจ้าหนี้ รายวัน */}
                {window.location.pathname === "/payables-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.payNo] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* วันที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.payDate]
                              ? formatDate(data[fieldMappings.payDate])
                              : "-"}
                          </td>

                          {/* เลขที่เอกสาร */}
                          <td className="text-center">
                            {data[fieldMappings.payNo] || "-"}
                          </td>

                          {/* ประเภท */}
                          <td className="text-center">
                            {data[fieldMappings.payStatusName] ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor:
                                    data[fieldMappings.payStatusName] ===
                                      "รอจ่าย"
                                      ? "#EAA300"
                                      : data[fieldMappings.payStatusName] ===
                                        "จ่ายแล้ว"
                                        ? "#13A10E"
                                        : "#808080", // ยกเลิก
                                  color: "white",
                                  width: "100%",
                                  fontSize: "12px",
                                  border: "none",
                                }}
                              >
                                {data[fieldMappings.payStatusName]}
                              </button>
                            ) : (
                              <center>
                                <p>-</p>
                              </center>
                            )}
                          </td>

                          {/* ประเภทการจ่าย */}
                          <td className="text-center">
                            {data[fieldMappings.payTypeName] || "-"}
                          </td>

                          {/* รหัสเจ้าหนี้ */}
                          <td className="text-center">
                            {data[fieldMappings.apCode] || "-"}
                          </td>

                          {/* ชื่อเจ้าหนี้ */}
                          <td className="text-left">
                            {data[fieldMappings.apName] || "-"}
                          </td>

                          {/* รายจ่ายสุทธิ */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.netTotal] || 0.0
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {window.location.pathname === "/payables-report" &&
                  currentItems.length > 0 && (
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        fontWeight: "bold",
                        borderTop: "2px solid #dee2e6",
                      }}
                    >
                      <td
                        className="text-end"
                        colSpan={6}
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        ยอดรวม
                      </td>
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) =>
                              sum +
                              (parseFloat(item[fieldMappings.netTotal]) || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  )}
                {/* 6.3 รายงานนำส่งรายได้ */}
                {window.location.pathname === "/vansale-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.Doc_No] || index + 1;
                      return (
                        <tr
                          key={rowId}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedRow === rowId
                                ? "rgb(105, 188, 216)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                          onClick={() => setSelectedRow(rowId)}
                        >
                          {/* เลขใบรับ */}
                          <td className="text-center">
                            {data[fieldMappings.Doc_No] || "-"}
                          </td>

                          {/* สถานะ */}
                          <td className="text-left">
                            {data[fieldMappings.AR_Name] || "-"}
                          </td>
                          {isIce ? (
                            <>
                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "101"
                                  ? data[fieldMappings.totalQty]
                                  : "-"}
                              </td>
                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "102"
                                  ? data[fieldMappings.totalQty]
                                  : "-"}
                              </td>
                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "103"
                                  ? data[fieldMappings.totalQty]
                                  : "-"}
                              </td>
                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "104"
                                  ? data[fieldMappings.totalQty]
                                  : "-"}
                              </td>
                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "105"
                                  ? data[fieldMappings.totalQty]
                                  : "-"}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="text-end">
                                {!["001", "002", "003", "005"].includes(
                                  data[fieldMappings.Item_Code]
                                )
                                  ? data[fieldMappings.totalQty]
                                  : "-"}
                              </td>

                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "001"
                                  ? data[fieldMappings.totalQty]
                                  : "-" || "-"}
                              </td>

                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "002"
                                  ? data[fieldMappings.totalQty]
                                  : "-" || "-"}
                              </td>

                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "003"
                                  ? data[fieldMappings.totalQty]
                                  : "-" || "-"}
                              </td>

                              <td className="text-end">
                                {data[fieldMappings.Item_Code] === "005"
                                  ? data[fieldMappings.totalQty]
                                  : "-" || "-"}
                              </td>
                            </>
                          )}

                          {/* รหัสเจ้าหนี้ */}
                          <td className="text-end">
                            {data[fieldMappings.Total_Before_Vat] || "-"}
                          </td>

                          {/* ชื่อเจ้าหนี้ */}
                          <td className="text-end">
                            {data[fieldMappings.VAT] || "-"}
                          </td>

                          {/* จำนวนเงิน */}
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.NetTotal] || 0.0
                            )}
                          </td>

                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.PaymentType] === "เงินสด"
                                ? data[fieldMappings.Pay_Total]
                                : 0.0 || 0.0
                            )}
                          </td>

                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.PaymentType] === "โอน"
                                ? data[fieldMappings.Pay_Total]
                                : 0.0 || 0.0
                            )}
                          </td>

                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.PaymentType] === "เครดิต"
                                ? data[fieldMappings.Pay_Total]
                                : 0.0 || 0.0
                            )}
                          </td>

                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.PaymentType] === "เช็ค"
                                ? data[fieldMappings.Pay_Total]
                                : 0.0 || 0.0
                            )}
                          </td>

                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.PaymentType] === "ค้างจ่าย"
                                ? data[fieldMappings.Pay_Total]
                                : 0.0 || 0.0
                            )}
                          </td>

                          <td className="text-left">
                            {data[fieldMappings.Driver1] || "-"}
                          </td>

                          <td className="text-center">
                            {(() => {
                              const roundMapping = {
                                1: "เช้า",
                                2: "บ่าย",
                                3: "เย็น",
                              };
                              return (
                                roundMapping[data[fieldMappings.PK_Round]] ||
                                "-"
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={17}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {/* 6.5 รายงานเบิกวัสดุภายใน */}
                {window.location.pathname ===
                  "/internal-material-requisition-report" &&
                  (currentItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const rowId = data[fieldMappings.docNo] || index + 1;
                      return (
                        <tr key={rowId}>
                          <td className="text-center">{index + 1}</td>
                          <td className="text-center">
                            {data[fieldMappings.docNo] || "-"}
                          </td>
                          <td className="text-center">
                            {data[fieldMappings.itemCode] || "-"}
                          </td>
                          <td className="text-left">
                            {data[fieldMappings.itemName] || "-"}
                          </td>
                          <td className="text-left">
                            {data[fieldMappings.itemDesc] || "-"}
                          </td>
                          <td className="text-center">
                            {data[fieldMappings.warehouse] || "-"}
                          </td>
                          <td className="text-center">
                            {data[fieldMappings.groupName] || "-"}
                          </td>
                          <td className="text-center">
                            {data[fieldMappings.itemQty] || "-"}
                          </td>
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.itemPriceUnit] || 0.0,
                              true
                            )}
                          </td>
                          <td className="text-center">
                            {data[fieldMappings.itemUnit] || "-"}
                          </td>
                          <td className="text-end">
                            {formatCurrency(
                              data[fieldMappings.itemTotal] || 0.0
                            )}
                          </td>
                          <td className="text-left">
                            {data[fieldMappings.empName] || "-"}
                          </td>
                          <td className="text-left">
                            {data[fieldMappings.deptName] || "-"}
                          </td>
                          <td className="text-left">
                            {data[fieldMappings.remark] || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={14}>
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  ))}
                {/* แถวยอดรวมสำหรับ sales-report ที่ปรับปรุงแล้ว */}
                {window.location.pathname === "/sales-report" &&
                  currentItems.length > 0 && (
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        fontWeight: "bold",
                        borderTop: "2px solid #dee2e6",
                      }}
                    >
                      {/* รวม 6 คอลัมน์แรกเป็น 1 คอลัมน์ว่าง */}
                      <td colSpan={6}></td>

                      {/* คอลัมน์ "ยอดรวม" ขนาดพอเหมาะ */}
                      <td
                        className="text-end"
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          width: "auto",
                        }}
                      >
                        ยอดรวม
                      </td>

                      {/* ยอดขาย */}
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) =>
                              sum + (item[fieldMappings.Total_Before_Vat] || 0),
                            0
                          )
                        )}
                      </td>
                      {/* VAT */}
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) => sum + (item[fieldMappings.VAT] || 0),
                            0
                          )
                        )}
                      </td>
                      {/* NetTotal */}
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) =>
                              sum + (item[fieldMappings.NetTotal] || 0),
                            0
                          )
                        )}
                      </td>
                      {/* PayTotal */}
                      <td
                        className="text-end"
                        style={{ fontWeight: "bold", fontSize: "14px" }}
                      >
                        {formatCurrency(
                          currentItems.reduce(
                            (sum, item) =>
                              sum + (item[fieldMappings.Pay_Total] || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
          {/* Footer แบบ VansaleReport */}
          <div className="d-flex justify-content-between align-items-center p-3">
            {/* ตัวเลือกจำนวนแถว */}
            <div>
              <select
                value={itemsPerPageDropdown}
                onChange={handleItemsPerPageChange}
                className="form-select"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* การแสดงจำนวนข้อมูล */}
            <div>
              <span>
                แสดง {startIndex}-{endIndex} ของ{" "}
                {dataMasterList.length.toLocaleString()} รายการ
              </span>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPageDropdown}
              dataMasterList={dataMasterList}
              handlePageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsTable;
